import { useMemo } from 'react';

import { Effectiveness } from 'Models/OperationalControls';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';

export interface ControlAssessmentBreakdown {
    unrated: number;
    fail: number;
    weak: number;
    moderate: number;
    strong: number;
    robust: number;
}

export interface ControlAssessmentStatistics {
    assessed: number;
    averageEffectiveness: Effectiveness;
    breakdown: ControlAssessmentBreakdown;
    total: number;
}

/** Used in conjunction with `ControlsDashlet`. */
export const useControlAssessmentStatistics = (assessments: TprmOrUcrmDueDiligenceAssessment[]): Record<string, ControlAssessmentStatistics> => {
    const statsByQuestionnaireId: Record<string, ControlAssessmentStatistics> = useMemo(() => {
        const stats: Record<string, ControlAssessmentStatistics> = {};

        assessments.forEach((questionnaire) => {
            const breakdown: ControlAssessmentBreakdown = {
                unrated: 0,
                fail: 0,
                moderate: 0,
                robust: 0,
                strong: 0,
                weak: 0,
            };

            for (const framework of questionnaire.control_frameworks) {
                for (const group of framework.control_groups) {
                    for (const control of group.controls) {
                        switch (control.control_assessment_human.effectiveness) {
                            case undefined:
                                breakdown.unrated++;
                                break;
                            case Effectiveness.FAIL:
                                breakdown.fail++;
                                break;
                            case Effectiveness.WEAK:
                                breakdown.weak++;
                                break;
                            case Effectiveness.MODERATE:
                                breakdown.moderate++;
                                break;
                            case Effectiveness.STRONG:
                                breakdown.strong++;
                                break;
                            case Effectiveness.ROBUST:
                                breakdown.robust++;
                                break;
                        }
                    }
                }
            }

            stats[questionnaire.id] = controlAssessmentStatisticsFromBreakdown(breakdown);
        });

        return stats;
    }, [assessments]);

    return statsByQuestionnaireId;
};

/** Derives the average effectiveness and percent complete from a breakdown of control effectiveness. */
const controlAssessmentStatisticsFromBreakdown = (breakdown: ControlAssessmentBreakdown): ControlAssessmentStatistics => {
    const count = breakdown.fail + breakdown.weak + breakdown.moderate + breakdown.strong + breakdown.robust;
    let averageEffectiveness = Effectiveness.UNRATED;
    if (count > 0) {
        const sum = breakdown.fail * Effectiveness.FAIL + breakdown.weak * Effectiveness.WEAK + breakdown.moderate * Effectiveness.MODERATE + breakdown.strong * Effectiveness.STRONG + breakdown.robust * Effectiveness.ROBUST;
        averageEffectiveness = Math.round(sum / count);
    }

    const total = breakdown.unrated + breakdown.fail + breakdown.weak + breakdown.moderate + breakdown.strong + breakdown.robust;
    const assessed = total - breakdown.unrated;

    return {
        assessed,
        averageEffectiveness,
        breakdown,
        total,
    };
};

/** Combines multiple `ControlAssessmentStatistics` into an overall summary. */
export const aggregateControlAssessmentStatistics = (statistics: ControlAssessmentStatistics[]): ControlAssessmentStatistics => {
    const summedBreakdown = statistics.reduce<ControlAssessmentBreakdown>(
        (aggregate, current) => ({
            unrated: aggregate.unrated + current.breakdown.unrated,
            fail: aggregate.fail + current.breakdown.fail,
            weak: aggregate.weak + current.breakdown.weak,
            moderate: aggregate.moderate + current.breakdown.moderate,
            strong: aggregate.strong + current.breakdown.strong,
            robust: aggregate.robust + current.breakdown.robust,
        }),
        { unrated: 0, fail: 0, weak: 0, moderate: 0, strong: 0, robust: 0 }
    );

    return controlAssessmentStatisticsFromBreakdown(summedBreakdown);
};
